package com.vizio.redwolf.pay.model

import kotlinx.serialization.SerialName
import kotlinx.serialization.Serializable
import kotlin.js.JsExport

@Serializable
@JsExport
data class Subscription(
    @SerialName("subscriptionId") val subscriptionId: String,
    @SerialName("customerId") val customerId: String,
    @SerialName("isActive") val isActive: Boolean,
    @SerialName("billingDate") val billingDate: String,
    @SerialName("unitAmount") val unitAmount: String? = null,
    @SerialName("addOnsTotal") val addOnsTotal: String? = null,
    @SerialName("total") val total: String? = null,
    @SerialName("status") val status: String,
    @SerialName("plan") val plan: Plan,
    @SerialName("addOns") val addOns: List<SubscribedAddOns>? = null,
    @SerialName("discount") val discount: String = "",
    @SerialName("pendingChange") val pendingChange: PendingChange? = null
)

@Serializable
@JsExport
data class SubscribedAddOns(
    @SerialName("code") val code: String,
    @SerialName("name") val name: String,
    @SerialName("price") val price: String
)

@Serializable
data class CouponCodes(
    @SerialName("currentPage") val currentPage: Int,
    @SerialName("pageSize") val pageSize: Int,
    @SerialName("totalPages") val totalPages: Int,
    @SerialName("totalCount") val totalCount: Int,
    @SerialName("data") val couponData: List<CouponData> = emptyList()
)

@Serializable
data class CouponData(
    val id: String,
    val partnerId: String,
    val appId: String,
    val couponCode: String,
    val name: String,
    val description: String,
    val planCodes: List<String>,
    val durationUnit: String? = null,
    val durationAmount: Int? = null,
    val multipleRedemptionsPerAccount: Boolean,
    val eligibleAccounts: String,
    val discountType: String,
    val discountAmount: String,
    val endDate: String? = null,
    val createdDate: String,
    val isActive: Boolean,
)

val Subscription.isCanceledOrExpired: Boolean
    get() = billingDate.isEmpty()

@Serializable
@JsExport
data class SubscriptionItem(
    @SerialName("subscriptionId") val subscriptionId: String?,  // null for previews
    @SerialName("billingPeriod") val billingPeriod: String,
    @SerialName("description") val description: String,
    @SerialName("subtotal") val subtotal: String,
    @SerialName("tax") val tax: String,
    @SerialName("total") val total: String,
    @SerialName("plan") val plan: Plan,
)

@Serializable
@JsExport
data class Credits(
    @SerialName("date") val date: String,
    @SerialName("appName") val appName: String,
    @SerialName("planName") val planName: String,
    @SerialName("amount") val amount: String
)

@Serializable
data class PreviewSubscriptionInfo(
    val planCode: String,
    val addOnCodes: List<String>? = null,
    val couponCode: String? = null,
    val noFreeTrial: Boolean? = null
)

@Serializable
data class PurchaseSubscriptionInfo(
    val planCode: String,
    val addOnCodes: List<String>? = null,
    val couponCode: String? = null,
    val partnerAccountId: String? = null,
    val noFreeTrial: Boolean? = null
)

@Serializable
data class CreateTransactionInfo(
    @SerialName("partnerId") val partnerId: String,
    @SerialName("appId") val appId: String,
    @SerialName("contentId") val contentId: String,
    @SerialName("orderType") val orderType: String,
    @SerialName("price") val price: Double,
    @SerialName("title") val title: String,
    @SerialName("episodeNumber") val episodeNumber: String? = null,
    @SerialName("seasonNumber") val seasonNumber: String? = null,
    @SerialName("isUHD") val isUHD: Boolean?
)

@Serializable
@JsExport
data class TransactionOrder(
    @SerialName("orderNumber") val orderNumber: String? = null,
    @SerialName("contentId") val contentId: String? = null,
    @SerialName("title") val title: String? = null,
    @SerialName("price") val price: Double,
    @SerialName("tax") val tax: Double,
    @SerialName("total") val total: Double
)

@Serializable
data class TransactionOrderPreview(
    @SerialName("price") val price: Double,
    @SerialName("tax") val tax: Double,
    @SerialName("total") val total: Double
)

@Serializable
@JsExport
data class SubscriptionItemPreview(
    val imageUrl: String,
    val name: String,
    val description: String,
    val terms: String,
    val unitAmount: String? = null,
    val invoiceAmount: String? = null,
    val addOns: List<AddOns>? = null,
    val bundleApps: List<BundleApp>? = null,
    val credit: String? = null,
    val price: String,
    val discount: String,
    val subtotal: String,
    val tax: String,
    val total: String
)

@Serializable
data class AddOns(
    val imageUrl: String? = null,
    val name: String? = null,
    val description: String? = null,
    val unitAmount: String? = null,
    val invoiceAmount: String? = null
)

@Serializable
data class BundleApp(
    val name: String? = null,
    val price: String? = null
)

@Serializable
data class CouponRequestInfo(
    val state: String? = null,
    val pageNumber: Int? = null,
    val pageSize: Int? = null,
)

@Serializable
@JsExport
data class SubscriptionInvoiceSummary(
    @SerialName("invoiceId") val invoiceId: String,
    @SerialName("lastFour") val lastFour: String,
    @SerialName("cardType") val cardType: String,
    @SerialName("cardTypeCode") val cardTypeCode: String,
    @SerialName("billingDate") val billingDate: String,
    @SerialName("total") val total: String,
    @SerialName("plan") val plan: Plan,
)

@Serializable
@JsExport
data class SubscriptionInvoice(
    @SerialName("invoiceNumber") val invoiceNumber: String,
    @SerialName("dueOn") val dueOn: String? = null,
    @SerialName("billedOn") val billedOn: String,
    @SerialName("invoiceAddress") val invoiceAddress: InvoiceAddress,
    @SerialName("subtotal") val subtotal: String,
    @SerialName("discount") val discount: String? = null,
    @SerialName("tax") val tax: String,
    @SerialName("taxRate") val taxRate: String?,
    @SerialName("taxState") val taxState: String?,
    @SerialName("total") val total: String,
    @SerialName("paid") val paid: String,
    @SerialName("balance") val balance: String,
    @SerialName("items") val items: List<SubscriptionItem> = emptyList(),
    @SerialName("payments") val payments: List<Payment> = emptyList(),
    @SerialName("credits") val credits: List<Credits> = emptyList()
)

@Serializable
@JsExport
data class Payment(
    @SerialName("date") val date: String,
    @SerialName("amount") val amount: String,
    @SerialName("cardType") val cardType: String,
    @SerialName("lastFour") val lastFour: String,
    val cardImageUrl: String
)

@Serializable
data class SubscriptionFromId(
    @SerialName("subscriptionId") val subscriptionId: String,
    @SerialName("createdDate") val createdDate: String,
    @SerialName("planCode") val planCode: String,
    @SerialName("status") val status: String,
    @SerialName("amount") val amount: Double,
)

@Serializable
data class PartnerPlansInfo(
    @SerialName("partner_id") val partnerId: String
)

@Serializable
@JsExport
data class PendingChange(
    @SerialName("activeDate") val activeDate: String,
    @SerialName("plan") val plan: Plan,
    val addOns: List<SubscribedAddOns>? = null
)

@Serializable
@JsExport
data class CreateOrder(
    @SerialName("orderNumber") val orderNumber: String? = null,
    @SerialName("partnerId") val partnerId: String,
    @SerialName("appId") val appId: String,
    @SerialName("contentId") val contentId: String,
    @SerialName("orderType") val orderType: String,
    @SerialName("title") val title: String,
    @SerialName("imageUrl") val imageUrl: String? = null,
    @SerialName("episodeNumber") val episodeNumber: String? = null,
    @SerialName("episodeName") val episodeName: String? = null,
    @SerialName("seasonNumber") val seasonNumber: String? = null,
    @SerialName("isUHD") val isUHD: Boolean? = null,
    @SerialName("price") val price: Double,
    @SerialName("tax") val tax: Double,
    @SerialName("total") val total: Double
)

@Serializable
@JsExport
data class OrderPreview(
    @SerialName("price") val price: Double,
    @SerialName("tax") val tax: Double,
    @SerialName("total") val total: Double
)

@Serializable
data class OrderPreviewInfo(
    val price: Double
)

@Serializable
data class CreateOrderInfo(
    val appId: String,
    val contentId: String,
    val orderType: String,
    val price: Double,
    val title: String,
    val partnerId: String? = null,
    val partnerAccountId: String? = null,
    val imageUrl: String? = null,
    val episodeNumber: String? = null,
    val episodeName: String? = null,
    val seasonNumber: String? = null,
    val isUHD: Boolean? = null
)

@Serializable
@JsExport
data class V2GetInvoices(
    val currentPage: Int,
    val pageSize: Int,
    val totalPages: Int,
    val totalCount: Int,
    @SerialName("data") val invoiceData: List<InvoiceData>? = null
)

@Serializable
@JsExport
data class InvoiceData(
    val invoiceId: String,
    val isSubscription: Boolean,
    val invoiceNumber: String? = null,
    val detailsLine1: String? = null,
    val detailsLine2: String? = null,
    val appImageUrl: String,
    val appName: String,
    val description: String,
    val cardTypeCode: String,
    val cardLastFour: String,
    val total: Double,
    val billingDate: String
)

@Serializable
@JsExport
data class V2GetInvoiceById(
    val invoiceNumber: String,
    val isSubscription: Boolean,
    var isRental: Boolean,
    val billingDate: String,
    val items: List<Item>,
    val subtotal: Double,
    val tax: Double,
    val discount: Double,
    val total: Double,
    val credits: Double,
    val paid: Double,
    val amountDue: Double,
    val cardTypeCode: String,
    val cardLastFour: String
)

@Serializable
@JsExport
data class Item(
    val appImageUrl: String,
    val appName: String,
    val description: String,
    val price: Double,
    val isAddOn: Boolean? = null
)